import { useQuery, useInfiniteQuery } from '@tanstack/react-query';

import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';

import type { Form, FormFilters, FormListResponse } from '@/types/form';

const KEYS = {
    all: ['forms'] as const,
    list: (filters: FormFilters) => [...KEYS.all, 'list', filters] as const,
    detail: (slug: string) => [...KEYS.all, 'detail', slug] as const,
};

export { KEYS as FORM_KEYS };

export function useForm(slug: string) {
    return useQuery({
        queryKey: KEYS.detail(slug),
        queryFn: async () => {
            const { data } = await api.get<Form>(API_ROUTES.formDetail(slug));
            return data;
        },
        enabled: !!slug,
    });
}

export function useForms(filters: FormFilters = {}, options: { enabled?: boolean } = {}) {
    return useInfiniteQuery({
        queryKey: KEYS.list(filters),
        enabled: options.enabled ?? true,
        queryFn: async ({ pageParam = 0 }) => {
            const params: Record<string, string | number | boolean> = {
                limit: 10,
                offset: pageParam,
            };

            if (filters.ordering) params.ordering = filters.ordering;
            if (filters.status && filters.status !== 'all') params.status = filters.status;
            if (filters.mine) params.mine = true;
            if (filters.search) params.search = filters.search;

            const { data } = await api.get<FormListResponse>(API_ROUTES.forms, { params });
            return data;
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage) => {
            if (!lastPage.next) return undefined;
            try {
                const url = new URL(lastPage.next);
                const offset = url.searchParams.get('offset');
                return offset ? parseInt(offset, 10) : undefined;
            } catch {
                return undefined;
            }
        },
    });
}
